<template>
  <div class="vanilla-g-wrap">
    <main class="screen" role="main" aria-labelledby="resume-heading">

      <span class="badge">Willkommen zurück</span>

      <div class="intro">
        <h1 id="resume-heading" class="intro-title">Schön, dass du wieder da bist</h1>
        <p class="intro-text">
          Du hast den Quiz schon zu einem grossen Teil ausgefüllt. Du kannst
          da weitermachen, wo du aufgehört hast.
        </p>
      </div>

      <div class="progress-card">
        <p class="progress-card-label">Dein bisheriger Fortschritt</p>

        <div class="progress-card-row">
          <span class="progress-card-count">
            <strong>{{ answered }}</strong>
            <span class="progress-card-of">von {{ totalQuestions }} Fragen</span>
          </span>
          <span class="progress-card-percent">{{ percent }}%</span>
        </div>

        <div
          class="progress-bar"
          role="progressbar"
          :aria-valuenow="doneCount"
          aria-valuemin="0"
          :aria-valuemax="sections.length"
        >
          <div
            v-for="s in sections"
            :key="s.title"
            class="progress-segment"
            :class="s.status"
            :title="s.tooltip || s.title"
          ></div>
        </div>

        <ul class="section-list">
          <li
            v-for="s in sections"
            :key="`item-${s.title}`"
            :class="s.status"
          >
            <span v-if="s.status === 'done'" class="check-icon">✓</span>
            <span v-if="s.status === 'current' || s.status === 'partial'">→</span>
            {{ s.title }}
          </li>
        </ul>
      </div>

      <p class="last-activity">Zuletzt aktiv: {{ lastActivity }}</p>

      <div class="actions">
        <button type="button" class="btn btn-primary" @click="resume">
          Weitermachen
        </button>
        <button type="button" class="btn btn-secondary" @click="showRestartModal = true">
          Von vorne beginnen
        </button>
        <button type="button" class="btn btn-tertiary" @click="remindLater">
          Später erinnern lassen
        </button>
      </div>

    </main>

    <div
      v-if="showRestartModal"
      class="modal-overlay"
      role="dialog"
      aria-labelledby="restart-modal-title"
      aria-modal="true"
      @click.self="showRestartModal = false"
    >
      <div class="modal">
        <h2 id="restart-modal-title" class="modal-title">Von vorne beginnen?</h2>
        <p class="modal-text">
          Deine {{ answered }} bisherigen Antworten gehen verloren. Diese Aktion
          kann nicht rückgängig gemacht werden.
        </p>
        <div class="modal-actions">
          <button type="button" class="btn btn-secondary" @click="showRestartModal = false">
            Abbrechen
          </button>
          <button type="button" class="btn btn-danger" @click="restart">
            Ja, neu starten
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VanillaG',
  data() {
    return {
      answered: 34,
      totalQuestions: 67,
      lastActivity: 'vor 2 Tagen, 14:32',
      showRestartModal: false,
      sections: [
        { title: 'Gemüse', status: 'done' },
        { title: 'Früchte', status: 'done' },
        { title: 'Tierische Proteine', status: 'done' },
        { title: 'Pflanzliche Proteine', status: 'partial', tooltip: 'Pflanzliche Proteine, teilweise' },
        { title: 'Getreide', status: '' },
        { title: 'Milchprodukte', status: '' },
        { title: 'Getränke', status: '' },
      ],
    };
  },
  computed: {
    percent() {
      return Math.round((this.answered / this.totalQuestions) * 100);
    },
    doneCount() {
      return this.sections.filter(s => s.status === 'done').length;
    },
  },
  methods: {
    resume() {
      this.$router.push('/quiz/vanilla/a');
    },
    restart() {
      this.showRestartModal = false;
      this.$router.push('/consent/age');
    },
    remindLater() {
      this.$router.push('/');
    },
  },
};
</script>

<style scoped>
.vanilla-g-wrap {
  --color-bg-page: #f5f4ef;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f1efe8;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.15);
  --color-accent: #534AB7;
  --color-accent-light: #EEEDFE;
  --color-good: #2D8659;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.5;
  min-height: 100%;
  padding: 1.25rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
}

.vanilla-g-wrap *,
.vanilla-g-wrap *::before,
.vanilla-g-wrap *::after {
  box-sizing: border-box;
}

.screen {
  width: 100%;
  max-width: 420px;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem 1.25rem;
  min-height: 600px;
  display: flex;
  flex-direction: column;
}

.badge {
  align-self: flex-start;
  background: var(--color-accent-light);
  color: var(--color-accent);
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: var(--radius-md);
  margin-bottom: 1rem;
  letter-spacing: 0.02em;
}

.intro {
  margin-bottom: 1.5rem;
}

.intro-title {
  font-size: 26px;
  font-weight: 500;
  margin: 0 0 12px;
  line-height: 1.2;
}

.intro-text {
  font-size: 15px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
}

.progress-card {
  background: var(--color-bg-muted);
  border-radius: var(--radius-md);
  padding: 18px 20px;
  margin-bottom: 1.25rem;
}

.progress-card-label {
  font-size: 11px;
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 500;
  margin: 0 0 10px;
}

.progress-card-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 14px;
}

.progress-card-count {
  font-size: 28px;
  font-weight: 500;
  color: var(--color-text-primary);
  line-height: 1;
}

.progress-card-count strong {
  color: var(--color-accent);
  font-weight: 500;
}

.progress-card-of {
  font-size: 14px;
  color: var(--color-text-tertiary);
}

.progress-card-percent {
  font-size: 13px;
  color: var(--color-text-secondary);
  font-weight: 500;
}

.progress-bar {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
}

.progress-segment {
  height: 8px;
  flex: 1;
  background: var(--color-bg-card);
  border-radius: 4px;
  border: 0.5px solid var(--color-border);
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
}

.progress-segment.done {
  background: var(--color-accent);
  border-color: var(--color-accent);
}

.progress-segment.partial {
  background: var(--color-bg-card);
  border-color: var(--color-accent);
}

.progress-segment.partial::after {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 60%;
  background: var(--color-accent);
}

.section-list {
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 12px;
  color: var(--color-text-tertiary);
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.section-list li {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.section-list li.done {
  color: var(--color-accent);
}

.section-list li.current,
.section-list li.partial {
  color: var(--color-text-primary);
  font-weight: 500;
}

.section-list .check-icon {
  color: var(--color-accent);
  font-weight: 600;
}

.last-activity {
  font-size: 13px;
  color: var(--color-text-tertiary);
  margin: 0 0 1.5rem;
  text-align: center;
  padding: 10px;
  border-top: 0.5px solid var(--color-border);
  border-bottom: 0.5px solid var(--color-border);
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: auto;
}

.btn {
  width: 100%;
  padding: 14px;
  border-radius: var(--radius-md);
  font-size: 15px;
  font-family: inherit;
  cursor: pointer;
  transition: opacity 0.15s, background 0.15s;
  border: 0.5px solid var(--color-border);
}

.btn-primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
  font-weight: 500;
}

.btn-primary:hover {
  opacity: 0.9;
}

.btn-secondary {
  background: var(--color-bg-card);
  color: var(--color-text-primary);
}

.btn-secondary:hover {
  background: var(--color-bg-muted);
}

.btn-tertiary {
  background: transparent;
  border: none;
  color: var(--color-text-tertiary);
  text-decoration: underline;
  text-underline-offset: 3px;
  font-size: 13px;
  padding: 8px;
}

.btn-tertiary:hover {
  color: var(--color-text-secondary);
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 2000;
}

.modal {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem;
  max-width: 360px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.modal-title {
  font-size: 18px;
  font-weight: 500;
  margin: 0 0 8px;
  color: var(--color-text-primary);
}

.modal-text {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  gap: 10px;
}

.modal-actions .btn {
  flex: 1;
  padding: 11px;
  font-size: 14px;
}

.btn-danger {
  background: #C8392E;
  color: white;
  border-color: #C8392E;
}

.btn-danger:hover {
  background: #A92F26;
  border-color: #A92F26;
}
</style>
